import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  ButtonLink,
  Column,
  DatePicker,
  DescriptionList,
  FormBox,
  InputCheckbox,
  InputMoney,
  InputRadio,
  InputText,
  Notice,
  PageActions,
  Row,
  Select,
  SelectLoading,
  Text,
  unMask,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "../../../../hooks/useService";
import ContractPatternModal from "../../../../components/ContractPatternModal";
import { availableServiceCodes } from "../../../../helpers/contractConstants";
import { getInvoiceDeadlineLabel, getPaymentRuleLabel, addOneYear, isCompleteDate } from "../../../../helpers";
import { closingDayLabel } from "@helpers/competencePeriod";
import { isoToDate, localIsoDate } from "@helpers/localDate";
import { escapeHtml, moneyInputText, usePageHeader } from "@components/ow";
import { remunerationLabel } from "../../Common/ContractDetail";
import { FormPage, FormPageSkeleton, FullWidth, focusFirstError } from "../../Common/FormPage";

// Formulário de contrato (modelo Formulário do DS OneWork), usado pelo cadastro
// (/c/contracts/new) e pela edição (/c/contracts/:id/edit). Sem `contractId` cria;
// com ele carrega o contrato e salva por cima.

const REMUNERATION_TYPES = ["FIXED", "VARIABLE", "HOURLY", "CLT"].map((value) => ({
  value,
  label: remunerationLabel(value),
}));

const MARKING_MODELS = [
  { value: "DAILY", label: "Diário" },
  { value: "WEEKLY", label: "Semanal" },
  { value: "MONTHLY", label: "Mensal" },
];

const TRACKING_MODES = [
  { value: "HOURS_ONLY", label: "Apenas horas trabalhadas" },
  { value: "HOURS_AND_ABSENCES", label: "Horas trabalhadas e dias ausentes" },
  { value: "ABSENCES_ONLY", label: "Apenas dias ausentes" },
  { value: "FIXED_WITH_HOUR_ADJUSTMENT", label: "Valor fixo com ajuste por horas" },
];

const DEADLINE_MONTHS = [
  { value: "CURRENT", label: "Mês vigente" },
  { value: "NEXT", label: "Mês seguinte" },
];

const WEEKDAYS = [
  { value: 0, label: "Dom" },
  { value: 1, label: "Seg" },
  { value: 2, label: "Ter" },
  { value: 3, label: "Qua" },
  { value: 4, label: "Qui" },
  { value: 5, label: "Sex" },
  { value: 6, label: "Sáb" },
];

// `short` é o texto do chip (o código); o rótulo inteiro fica no menu
const SERVICE_CODES = availableServiceCodes.map((c) => ({ value: c.code, short: c.code, label: c.label }));
const KNOWN_CODES = new Set(SERVICE_CODES.map((c) => c.value));

const FIXED_MODE = "FIXED_WITH_HOUR_ADJUSTMENT";
const CLT_DAY = /^contract-clt-day-(\d)$/;

/** Grupo (radio ou checkbox) com a frase de apoio ou o erro logo embaixo, sem a margem do fieldset da lib. */
// O `&&` vence o `> div { gap: 25px }` que a FormBox da lib põe nos filhos diretos.
const Stack = styled.div`
  && {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  fieldset {
    margin-bottom: 0;
  }
`;

/** Rótulo com uma linha de apoio embaixo (razão social, regra do padrão), no menu do Select. */
const OptionText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

/** Opção do Select da lib para o valor em texto do estado (vazio → nenhuma). */
const pick = (options, value) => options.find((option) => option.value === String(value)) || null;

/** Valor do InputMoney ("R$ 5.000,00") em número; vazio ou "R$ 0,00" → 0. */
const moneyValue = (text) => (text ? Number(unMask(text, true)) || 0 : 0);

/** Sem acento e em minúsculas, para a busca do Select. */
const normalize = (text) =>
  String(text || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/** Busca no rótulo e na linha de apoio (prestador por razão social, código por descrição). */
const searchLabelAndHint = (option, input) =>
  normalize(`${option.data.label} ${option.data.hint || ""}`).includes(normalize(input));

/** Menu com o rótulo e a linha de apoio; no campo, só o rótulo. */
function labelWithHint(option, { context }) {
  if (context === "value" || !option.hint) return option.label;
  return (
    <OptionText>
      <Text $text={escapeHtml(option.label)} $variant="paragraph_small_medium" $color="N900" />
      <Text $text={escapeHtml(option.hint)} $variant="caption_medium" $color="N400" />
    </OptionText>
  );
}

/** Chip com o código curto; no menu, o código com a descrição. */
const serviceCodeLabel = (option, { context }) => (context === "value" ? option.short : option.label);

/** Número com no máximo `step` de precisão (o `step` do input antigo, que o navegador conferia). */
function offStep(value, step, base = 0) {
  const n = parseFloat(value);
  if (Number.isNaN(n)) return false;
  const k = (n - base) / step;
  return Math.abs(k - Math.round(k)) > 1e-9;
}

/** Erro de um grupo (radio ou checkbox), que a lib só pinta de vermelho, sem a mensagem. */
function GroupError({ message }) {
  return message ? <Text $text={message} $variant="caption_medium" $color="E400" /> : null;
}

export default function ContractForm({ contractId }) {
  const isEdit = !!contractId;
  const navigate = useNavigate();
  const contractService = useService("contracts");
  const userService = useService("users");
  const projectService = useService("projects");
  const patternService = useService("contractPatterns");
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [availablePatterns, setAvailablePatterns] = useState([]);
  const [showPatternModal, setShowPatternModal] = useState(false);
  const [contractor, setContractor] = useState(null);

  // Os erros aparecem nos campos depois da primeira tentativa e se atualizam a cada mudança
  const [submitted, setSubmitted] = useState(false);

  // Campos do formulário
  const [userId, setUserId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [renewalDate, setRenewalDate] = useState("");
  const [autoRenew, setAutoRenew] = useState(false);
  const [lastRenewalDate, setLastRenewalDate] = useState("");
  const [remunerationType, setRemunerationType] = useState("");
  const [fixedValue, setFixedValue] = useState("");
  const [hourlyRate, setHourlyRate] = useState("");
  const [hourMarkingModel, setHourMarkingModel] = useState("");
  const [hourTrackingMode, setHourTrackingMode] = useState("HOURS_ONLY");
  const [expectedMonthlyHours, setExpectedMonthlyHours] = useState("176");
  const [expectedDailyHours, setExpectedDailyHours] = useState("8");
  const [projectId, setProjectId] = useState("");
  const [valueInputDeadline, setValueInputDeadline] = useState("");
  const [valueInputDeadlineMonth, setValueInputDeadlineMonth] = useState("CURRENT");
  const [requiresValueApproval, setRequiresValueApproval] = useState(false);
  const [contractPatternId, setContractPatternId] = useState("");
  const [serviceCodes, setServiceCodes] = useState([]);
  const [hasVacationRight, setHasVacationRight] = useState(false);
  const [cltDailyHours, setCltDailyHours] = useState("8");
  const [cltWorkdays, setCltWorkdays] = useState([1, 2, 3, 4, 5]);

  const backHref = isEdit ? `/c/contracts/${contractId}` : "/c/contracts";

  usePageHeader({
    title: isEdit ? "Editar contrato" : "Novo contrato",
    breadcrumb: isEdit
      ? [
          { page: "Contratos", route: "/c/contracts" },
          { page: (contractor && contractor.name) || "Contrato", route: backHref },
          { page: "Editar", route: `/c/contracts/${contractId}/edit` },
        ]
      : [
          { page: "Contratos", route: "/c/contracts" },
          { page: "Novo contrato", route: "/c/contracts/new" },
        ],
  });

  useEffect(() => {
    fetchUsers();
    fetchProjects();
    fetchPatterns();
    if (isEdit) fetchContract();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchUsers() {
    try {
      const result = await userService.list("role=EMP&limit=9999");
      if (result && result.data && result.data.list) {
        setAvailableUsers(result.data.list);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar lista de colaboradores");
    }
    setLoadingUsers(false);
  }

  async function fetchProjects() {
    setLoadingProjects(true);
    try {
      const result = await projectService.list("status=ACTIVE&limit=9999");
      if (result && result.data && result.data.list) {
        setAvailableProjects(result.data.list);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar lista de projetos");
    }
    setLoadingProjects(false);
  }

  async function fetchPatterns() {
    try {
      const result = await patternService.listAll("limit=9999");
      if (result && result.data) {
        setAvailablePatterns(result.data);
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function fetchContract() {
    try {
      const result = await contractService.get(contractId);
      if (result && result.data) {
        const contract = result.data;

        // Preencher os campos com os dados do contrato
        setContractor(contract.contractor || null);
        setUserId(contract.user_id || "");
        setStartDate(contract.start_date ? contract.start_date.split("T")[0] : "");
        const renewal = contract.renewal_date ? contract.renewal_date.split("T")[0] : "";
        setRenewalDate(renewal);
        setAutoRenew(!renewal);
        setLastRenewalDate(renewal);
        setRemunerationType(contract.remuneration_type || "");
        setFixedValue(moneyInputText(contract.fixed_value));
        setHourlyRate(moneyInputText(contract.hourly_rate));
        setHourMarkingModel(contract.hour_marking_model || "");
        setHourTrackingMode(contract.hour_tracking_mode || "HOURS_ONLY");
        setExpectedMonthlyHours(contract.expected_monthly_hours || "176");
        setExpectedDailyHours(contract.expected_daily_hours || "8");
        setProjectId(contract.project_id || "");
        setValueInputDeadline(contract.value_input_deadline || "");
        setValueInputDeadlineMonth(contract.value_input_deadline_month || "CURRENT");
        setRequiresValueApproval(contract.requires_value_approval || false);
        setContractPatternId(contract.contract_pattern_id ? String(contract.contract_pattern_id) : "");
        setServiceCodes(contract.service_codes || []);
        setCltDailyHours(contract.clt_daily_hours || "8");
        setCltWorkdays(
          Array.isArray(contract.clt_workdays)
            ? contract.clt_workdays.map(Number)
            : [1, 2, 3, 4, 5]
        );

        // Direitos adicionais
        if (contract.additional_rights) {
          setHasVacationRight(contract.additional_rights.vacation || false);
        }
      } else {
        throw new Error("Erro ao carregar dados do contrato");
      }
    } catch (error) {
      console.error(error);
      const errorMessage = error?.response?.data?.msg || error?.message || "Erro ao carregar contrato";
      toast.error(errorMessage);
      navigate("/c/contracts");
    }
    setLoading(false);
  }

  function getSelectedPattern() {
    return availablePatterns.find((p) => p.id === parseInt(contractPatternId));
  }

  function handlePatternCreated(newPattern) {
    fetchPatterns();
    if (newPattern && newPattern.id) {
      setContractPatternId(String(newPattern.id));
    }
  }

  // O checkbox da lib avisa duas vezes (no input e no fieldset): marcar, e não inverter.
  function setCltWorkday(day, checked) {
    setCltWorkdays((prev) => {
      const others = prev.filter((d) => d !== day);
      return checked ? [...others, day].sort() : others;
    });
  }

  function handleStartDateChange(value) {
    setStartDate(value);
    if (!autoRenew && isCompleteDate(value) && !renewalDate) {
      const suggested = addOneYear(value);
      setRenewalDate(suggested);
      setLastRenewalDate(suggested);
    }
  }

  function handleAutoRenewChange(checked) {
    setAutoRenew(checked);
    if (checked) {
      if (renewalDate) setLastRenewalDate(renewalDate);
      setRenewalDate("");
    } else {
      if (lastRenewalDate) {
        setRenewalDate(lastRenewalDate);
      } else if (isCompleteDate(startDate)) {
        const suggested = addOneYear(startDate);
        setRenewalDate(suggested);
        setLastRenewalDate(suggested);
      }
    }
  }

  function handleRenewalDateChange(value) {
    setRenewalDate(value);
    setLastRenewalDate(value);
  }

  // Códigos que não estão na lista fixa (vindos do contrato) não somem ao editar.
  function handleServiceCodesChange(options) {
    const values = (options || []).map((option) => option.value);
    setServiceCodes([...serviceCodes.filter((code) => !KNOWN_CODES.has(code)), ...values]);
  }

  /**
   * As mesmas regras de antes, mais as que o navegador conferia pelo
   * `required`/`min`/`max`/`step` dos inputs antigos. Na ordem dos campos na tela;
   * `field` é o id do campo, onde a mensagem aparece. Os valores em R$ vêm do
   * InputMoney, que não deixa passar de 2 casas; "R$ 0,00" conta como vazio.
   */
  function validate() {
    const errors = [];
    const add = (field, label, message) => errors.push({ field, label, message });
    const isHourly = remunerationType === "HOURLY";
    const fixedMode = isHourly && hourTrackingMode === FIXED_MODE;

    if (!userId) add("contract-user", "Prestador", "selecione um prestador");
    if (!startDate) add("contract-start", "Início", "informe a data de início do contrato");
    if (!autoRenew && !renewalDate) add("contract-renewal", "Renovação", "informe a data de renovação do contrato");
    if (!remunerationType) add("contract-type", "Tipo de remuneração", "selecione o tipo de remuneração");

    if (remunerationType === "FIXED" && moneyValue(fixedValue) <= 0) {
      add("contract-fixed", "Valor fixo mensal", "informe o valor fixo mensal");
    }

    if (remunerationType === "CLT") {
      if (!cltDailyHours || parseFloat(cltDailyHours) <= 0) add("contract-clt-hours", "Jornada diária", "informe a jornada diária");
      else if (parseFloat(cltDailyHours) < 1 || parseFloat(cltDailyHours) > 12) add("contract-clt-hours", "Jornada diária", "informe entre 1 e 12 horas");
      else if (offStep(cltDailyHours, 0.5, 1)) add("contract-clt-hours", "Jornada diária", "use horas inteiras ou meias horas (ex.: 7.5)");
      if (cltWorkdays.length === 0) add("contract-clt-days", "Dias da semana trabalhados", "selecione ao menos um dia da semana trabalhado");
    }

    if (isHourly) {
      if (moneyValue(hourlyRate) <= 0) add("contract-hourly", "Valor por hora", "informe o valor por hora");
      if (!hourMarkingModel) add("contract-marking", "Modelo de marcação", "selecione o modelo de marcação de horas");
      if (fixedMode && moneyValue(fixedValue) <= 0) add("contract-fixed", "Valor fixo mensal", "informe o valor fixo mensal");
      if (hourTrackingMode === "ABSENCES_ONLY" && expectedMonthlyHours && offStep(expectedMonthlyHours, 0.5)) {
        add("contract-monthly-hours", "Horas esperadas por mês", "use horas inteiras ou meias horas (ex.: 175.5)");
      }
      if (fixedMode && (!expectedDailyHours || parseFloat(expectedDailyHours) <= 0)) {
        add("contract-daily-hours", "Horas por dia", "informe as horas por dia");
      } else if (expectedDailyHours && offStep(expectedDailyHours, 0.5)) {
        add("contract-daily-hours", "Horas por dia", "use horas inteiras ou meias horas (ex.: 7.5)");
      }
    }

    if (isHourly && !projectId) add("contract-project", "Projeto", "selecione um projeto para contratos por hora");

    if (remunerationType === "VARIABLE" && valueInputDeadline) {
      const day = parseFloat(valueInputDeadline);
      if (!Number.isInteger(day) || day < 1 || day > 31) add("contract-deadline", "Prazo para informar o valor", "informe um dia entre 1 e 31");
    }

    if (!contractPatternId && remunerationType !== "CLT") add("contract-pattern", "Padrão de contrato", "selecione um padrão de contrato");

    return errors;
  }

  const fieldErrors = {};
  if (submitted) {
    validate().forEach((e) => {
      if (!fieldErrors[e.field]) fieldErrors[e.field] = `${capitalize(e.message)}.`;
    });
  }

  async function handleSubmit() {
    setSubmitted(true);
    const errors = validate();
    if (errors.length) {
      focusFirstError(errors.map((e) => e.field));
      return;
    }

    setSaving(true);

    try {
      const formData = {
        user_id: userId,
        start_date: startDate,
        renewal_date: autoRenew ? null : renewalDate,
        remuneration_type: remunerationType,
        fixed_value:
          remunerationType === "FIXED" ||
          (remunerationType === "HOURLY" &&
            hourTrackingMode === "FIXED_WITH_HOUR_ADJUSTMENT")
            ? moneyValue(fixedValue)
            : null,
        hourly_rate: remunerationType === "HOURLY" ? moneyValue(hourlyRate) : null,
        hour_marking_model: remunerationType === "HOURLY" ? hourMarkingModel : null,
        hour_tracking_mode: remunerationType === "HOURLY" ? hourTrackingMode : null,
        expected_monthly_hours: remunerationType === "HOURLY" && hourTrackingMode === "ABSENCES_ONLY" ? parseFloat(expectedMonthlyHours) || 176 : null,
        expected_daily_hours: remunerationType === "HOURLY" ? parseFloat(expectedDailyHours) || 8 : null,
        project_id: projectId || null, // Aceita projeto em todos os tipos
        value_input_deadline: valueInputDeadline ? parseInt(valueInputDeadline) : null,
        value_input_deadline_month: valueInputDeadline ? valueInputDeadlineMonth : "CURRENT",
        requires_value_approval: remunerationType === "VARIABLE" ? requiresValueApproval : false,
        contract_pattern_id: contractPatternId ? parseInt(contractPatternId) : null,
        clt_daily_hours: remunerationType === "CLT" ? parseFloat(cltDailyHours) : null,
        clt_workdays: remunerationType === "CLT" ? cltWorkdays : null,
        service_codes: serviceCodes.length > 0 ? serviceCodes : null,
        additional_rights: {
          vacation: hasVacationRight,
        },
      };

      const res = isEdit
        ? await contractService.update(formData, contractId)
        : await contractService.create(formData);

      if (res && !res.error) {
        toast.success(isEdit ? "Contrato atualizado com sucesso!" : "Contrato criado com sucesso!");
        navigate(isEdit ? `/c/contracts/${contractId}` : "/c/contracts");
        return;
      }
      throw new Error(res?.msg || (isEdit ? "Erro ao atualizar contrato" : "Erro ao criar contrato"));
    } catch (error) {
      console.error(error);
      const fallback = isEdit
        ? "Erro ao atualizar contrato. Verifique os dados e tente novamente."
        : "Erro ao criar contrato. Verifique os dados e tente novamente.";
      const errorMessage = error?.response?.data?.msg || error?.message || fallback;
      toast.error(errorMessage);
    }

    setSaving(false);
  }

  const userOptions = availableUsers.map((user) => ({
    value: String(user.id),
    label: user.name,
    hint: user.company_name || undefined,
  }));
  const projectOptions = availableProjects.map((project) => ({
    value: String(project.id),
    label: project.name,
  }));
  const patternOptions = availablePatterns.map((pattern) => ({
    value: String(pattern.id),
    label: pattern.name,
    hint: `Pagamento: ${getPaymentRuleLabel(pattern.payment_rule_type, pattern.payment_rule_value, pattern.payment_rule_month)} · NF: ${getInvoiceDeadlineLabel(pattern.invoice_deadline_type, pattern.invoice_deadline, pattern.invoice_deadline_month)}`,
  }));

  if (loading) return <FormPageSkeleton sections={[200, 260, 160, 160]} />;

  const isHourly = remunerationType === "HOURLY";
  const fixedMode = isHourly && hourTrackingMode === FIXED_MODE;
  const showPattern = remunerationType !== "CLT";
  const selectedPattern = contractPatternId ? getSelectedPattern() : null;

  const patternSummary = selectedPattern
    ? [
        {
          label: "Prazo da nota fiscal",
          value: getInvoiceDeadlineLabel(selectedPattern.invoice_deadline_type, selectedPattern.invoice_deadline, selectedPattern.invoice_deadline_month),
        },
        {
          label: "Regra de pagamento",
          value: getPaymentRuleLabel(selectedPattern.payment_rule_type, selectedPattern.payment_rule_value, selectedPattern.payment_rule_month),
        },
        ...(isHourly ? [{ label: "Apuração de horas", value: capitalize(closingDayLabel(selectedPattern.closing_day)) }] : []),
      ]
    : [];

  const projectField = (
    <Select
      $idForm="contract-project"
      $label="Projeto"
      $isRequired={isHourly}
      $hasTooltip={isHourly ? "Obrigatório em contratos por hora." : "Opcional: organiza os relatórios por projeto."}
      $isClearable
      $isDisabled={loadingProjects}
      $options={projectOptions}
      $errors={fieldErrors["contract-project"]}
      placeholder="Selecione um projeto"
      value={pick(projectOptions, projectId)}
      onChange={(option) => setProjectId(option ? option.value : "")}
    />
  );

  const fixedValueField = (
    <InputMoney
      $idForm="contract-fixed"
      $label="Valor fixo mensal"
      $variant="default"
      $isRequired
      $hasTooltip={fixedMode ? "Valor cheio do mês quando a jornada é cumprida." : undefined}
      $errors={fieldErrors["contract-fixed"]}
      placeholder="R$ 0,00"
      inputMode="numeric"
      value={fixedValue}
      onChange={(e) => setFixedValue(e.target.value)}
    />
  );

  return (
    <>
      <FormPage
        actions={
          <PageActions>
            <ButtonLink $text="Cancelar" $action={() => navigate(backHref)} />
            <Button
              $text={isEdit ? "Salvar alterações" : "Criar contrato"}
              $variant="primary"
              $action={handleSubmit}
              $isLoading={saving}
            />
          </PageActions>
        }
      >
        <FormBox title="Prestador e vigência">
          <Row $gap={20}>
            <Column $columns={6}>
              {loadingUsers ? (
                <SelectLoading $idForm="contract-user" $label="Prestador" placeholder="Selecione um prestador" />
              ) : (
                <Select
                  $idForm="contract-user"
                  $label="Prestador"
                  $isRequired
                  $hasTooltip={
                    isEdit
                      ? "O prestador não pode ser alterado depois que o contrato é criado."
                      : "Só colaboradores prestadores de serviço."
                  }
                  $isClearable={!isEdit}
                  $isDisabled={isEdit}
                  $options={userOptions}
                  $errors={fieldErrors["contract-user"]}
                  placeholder="Selecione um prestador"
                  value={pick(userOptions, userId)}
                  onChange={(option) => setUserId(option ? option.value : "")}
                  formatOptionLabel={labelWithHint}
                  filterOption={searchLabelAndHint}
                />
              )}
            </Column>
            <Column $columns={3}>
              <DatePicker
                $idForm="contract-start"
                $label="Início"
                $variant="default"
                $isRequired
                $errors={fieldErrors["contract-start"]}
                $popperPlacement="bottom-start"
                $date={isoToDate(startDate)}
                $setDate={(date) => handleStartDateChange(localIsoDate(date) || "")}
              />
            </Column>
            {!autoRenew && (
              <Column $columns={3}>
                <DatePicker
                  $idForm="contract-renewal"
                  $label="Renovação"
                  $variant="default"
                  $isRequired
                  $hasTooltip="Sugerido: início + 1 ano."
                  $errors={fieldErrors["contract-renewal"]}
                  $popperPlacement="bottom-start"
                  $date={isoToDate(renewalDate)}
                  $setDate={(date) => handleRenewalDateChange(localIsoDate(date) || "")}
                />
              </Column>
            )}
          </Row>
          <Stack>
            <InputCheckbox
              $options={[{ name: "contract-auto-renew", label: "Renovação automática", value: autoRenew }]}
              onChange={(e) => e.target.name === "contract-auto-renew" && handleAutoRenewChange(e.target.checked)}
            />
            <Text $text="Se marcado, o contrato não possui data de término." $variant="caption_medium" $color="N400" />
          </Stack>
        </FormBox>

        <FormBox title="Remuneração">
          <Stack>
            <InputRadio
              name="contract-type"
              $legend="Tipo de remuneração"
              $isRequired
              $errors={fieldErrors["contract-type"]}
              $options={REMUNERATION_TYPES}
              value={remunerationType}
              onChange={(e) => e.target.name === "contract-type" && setRemunerationType(e.target.value)}
            />
            <GroupError message={fieldErrors["contract-type"]} />
          </Stack>

          {remunerationType === "FIXED" && (
            <Row $gap={20}>
              <Column $columns={4}>{fixedValueField}</Column>
              <Column $columns={4}>{projectField}</Column>
            </Row>
          )}

          {remunerationType === "CLT" && (
            <>
              <Row $gap={20}>
                <Column $columns={4}>
                  <InputText
                    $idForm="contract-clt-hours"
                    $label="Jornada diária (horas)"
                    $variant="default"
                    $isRequired
                    $hasTooltip="Horas esperadas por dia trabalhado."
                    $errors={fieldErrors["contract-clt-hours"]}
                    type="number"
                    step="0.5"
                    min="1"
                    max="12"
                    placeholder="Ex.: 8"
                    value={cltDailyHours}
                    onChange={(e) => setCltDailyHours(e.target.value)}
                  />
                </Column>
                <Column $columns={4}>{projectField}</Column>
              </Row>
              <Stack>
                <InputCheckbox
                  $legend="Dias da semana trabalhados"
                  $isRequired
                  $errors={fieldErrors["contract-clt-days"]}
                  $options={WEEKDAYS.map((day) => ({
                    name: `contract-clt-day-${day.value}`,
                    label: day.label,
                    value: cltWorkdays.includes(day.value),
                  }))}
                  onChange={(e) => {
                    const match = e.target.name.match(CLT_DAY);
                    if (match) setCltWorkday(Number(match[1]), e.target.checked);
                  }}
                />
                <GroupError message={fieldErrors["contract-clt-days"]} />
              </Stack>
              <Notice
                $variant="gray"
                $text="Contratos CLT liberam a marcação de ponto para o colaborador e não geram cronograma de pagamento nem pedem nota fiscal."
              />
            </>
          )}

          {isHourly && (
            <>
              <Row $gap={20}>
                <Column $columns={4}>{projectField}</Column>
                <Column $columns={4}>
                  <InputMoney
                    $idForm="contract-hourly"
                    $label="Valor por hora"
                    $variant="default"
                    $isRequired
                    $errors={fieldErrors["contract-hourly"]}
                    placeholder="R$ 0,00"
                    inputMode="numeric"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(e.target.value)}
                  />
                </Column>
                <Column $columns={4}>
                  <Select
                    $idForm="contract-marking"
                    $label="Modelo de marcação"
                    $isRequired
                    $options={MARKING_MODELS}
                    $errors={fieldErrors["contract-marking"]}
                    value={pick(MARKING_MODELS, hourMarkingModel)}
                    onChange={(option) => setHourMarkingModel(option ? option.value : "")}
                  />
                </Column>
              </Row>
              <Row $gap={20}>
                <Column $columns={4}>
                  <Select
                    $idForm="contract-tracking"
                    $label="Acompanhamento"
                    $hasTooltip={
                      fixedMode
                        ? "O prestador marca as horas normalmente. O que passar ou faltar da jornada esperada é pago ou descontado pelo valor da hora."
                        : "Define o que o prestador registra neste contrato."
                    }
                    $options={TRACKING_MODES}
                    value={pick(TRACKING_MODES, hourTrackingMode)}
                    onChange={(option) => option && setHourTrackingMode(option.value)}
                  />
                </Column>
                <Column $columns={4}>
                  <InputText
                    $idForm="contract-daily-hours"
                    $label="Horas por dia"
                    $variant="default"
                    $isRequired={fixedMode}
                    $hasTooltip={
                      fixedMode
                        ? "Jornada esperada em cada dia útil. É a base para calcular faltas e extras."
                        : "Converte dias em horas quando o padrão tem dia de fechamento: dimensiona o adiantamento do fim do mês e a proporcionalidade no início e no fim do contrato."
                    }
                    $errors={fieldErrors["contract-daily-hours"]}
                    type="number"
                    step="0.5"
                    placeholder="Ex.: 8"
                    value={expectedDailyHours}
                    onChange={(e) => setExpectedDailyHours(e.target.value)}
                  />
                </Column>
                {fixedMode && <Column $columns={4}>{fixedValueField}</Column>}
                {hourTrackingMode === "ABSENCES_ONLY" && (
                  <Column $columns={4}>
                    <InputText
                      $idForm="contract-monthly-hours"
                      $label="Horas esperadas por mês"
                      $variant="default"
                      $hasTooltip="Padrão: 176 (22 dias × 8h)."
                      $errors={fieldErrors["contract-monthly-hours"]}
                      type="number"
                      step="0.5"
                      placeholder="Ex.: 176"
                      value={expectedMonthlyHours}
                      onChange={(e) => setExpectedMonthlyHours(e.target.value)}
                    />
                  </Column>
                )}
              </Row>
              {fixedMode && (
                <Notice
                  $variant="blue"
                  $title="Como o valor é calculado"
                  $text="Valor fixo + (horas trabalhadas − jornada esperada do mês) × valor da hora. Feriados nacionais e folgas cadastradas pela empresa já entram no cálculo da jornada esperada. Nos meses de entrada e saída o valor fixo é proporcional aos dias úteis do contrato. O valor nunca fica negativo."
                />
              )}
            </>
          )}

          {remunerationType === "VARIABLE" && (
            <>
              <Row $gap={20}>
                <Column $columns={4}>{projectField}</Column>
                <Column $columns={4}>
                  <InputText
                    $idForm="contract-deadline"
                    $label="Prazo para informar o valor (dia do mês)"
                    $variant="default"
                    $hasTooltip="Dia limite para informar o valor da competência."
                    $errors={fieldErrors["contract-deadline"]}
                    type="number"
                    min="1"
                    max="31"
                    placeholder="Ex.: 5"
                    value={valueInputDeadline}
                    onChange={(e) => setValueInputDeadline(e.target.value)}
                  />
                </Column>
                {valueInputDeadline && (
                  <Column $columns={4}>
                    <Select
                      $idForm="contract-deadline-month"
                      $label="Mês do prazo"
                      $hasTooltip="Se o prazo cai no próprio mês trabalhado ou no seguinte."
                      $options={DEADLINE_MONTHS}
                      value={pick(DEADLINE_MONTHS, valueInputDeadlineMonth)}
                      onChange={(option) => option && setValueInputDeadlineMonth(option.value)}
                    />
                  </Column>
                )}
              </Row>
              <Stack>
                <InputCheckbox
                  $options={[{ name: "contract-double-check", label: "Exige duplo check", value: requiresValueApproval }]}
                  onChange={(e) => e.target.name === "contract-double-check" && setRequiresValueApproval(e.target.checked)}
                />
                <Text
                  $text="Se marcado, o valor informado precisa ser aprovado por outra pessoa da empresa antes de liberar a nota fiscal."
                  $variant="caption_medium"
                  $color="N400"
                />
              </Stack>
            </>
          )}
        </FormBox>

        {showPattern && (
          <FormBox title="Padrão de contrato">
            <Row $gap={20} $alignItems="center">
              <Column $columns={6}>
                <Select
                  $idForm="contract-pattern"
                  $label="Padrão"
                  $isRequired
                  $hasTooltip="Prazo da nota fiscal, regra de pagamento e apuração de horas que valem para este contrato."
                  $options={patternOptions}
                  $errors={fieldErrors["contract-pattern"]}
                  placeholder="Selecione um padrão"
                  value={pick(patternOptions, contractPatternId)}
                  onChange={(option) => setContractPatternId(option ? option.value : "")}
                  formatOptionLabel={labelWithHint}
                  filterOption={searchLabelAndHint}
                />
              </Column>
              <Column $withoutColumns>
                <Button
                  $text="Criar padrão"
                  $variant="outline"
                  $size="small"
                  $noWidth
                  $action={() => setShowPatternModal(true)}
                />
              </Column>
            </Row>
            {patternSummary.length > 0 && (
              <FullWidth>
                <DescriptionList $items={patternSummary} $columns={3} />
              </FullWidth>
            )}
          </FormBox>
        )}

        <FormBox title="Informações adicionais">
          <Row>
            <Column $columns={12}>
              <Select
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                $idForm="contract-service-codes"
                $label="Códigos de serviço aceitos"
                $hasTooltip="Códigos CNAE ou municipais aceitos nas notas fiscais deste contrato."
                $options={SERVICE_CODES}
                placeholder="Selecione os códigos de serviço"
                value={SERVICE_CODES.filter((option) => serviceCodes.includes(option.value))}
                onChange={handleServiceCodesChange}
                formatOptionLabel={serviceCodeLabel}
              />
            </Column>
          </Row>
          <InputCheckbox
            $legend="Direitos adicionais"
            $options={[{ name: "contract-vacation", label: "Direito a férias", value: hasVacationRight }]}
            onChange={(e) => e.target.name === "contract-vacation" && setHasVacationRight(e.target.checked)}
          />
        </FormBox>
      </FormPage>

      <ContractPatternModal
        show={showPatternModal}
        onHide={() => setShowPatternModal(false)}
        onCreated={handlePatternCreated}
      />
    </>
  );
}
